import * as THREE from 'three';

/**
 * ICO-style hand bond: soft glowing connector + two-bone CCD IK helpers.
 * Mixamo has no dedicated "hold hands" locomotion; we meet hands in world space.
 */

const _bonePos = new THREE.Vector3();
const _effPos = new THREE.Vector3();
const _toEff = new THREE.Vector3();
const _toTgt = new THREE.Vector3();
const _axis = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _qWorld = new THREE.Quaternion();
const _qParent = new THREE.Quaternion();
const _qLocal = new THREE.Quaternion();

export type ArmSide = 'left' | 'right';

export type ArmChain = {
  upper: THREE.Bone;
  mid: THREE.Bone;
  hand: THREE.Bone;
};

/** Find Mixamo arm chain on a loaded character root. */
export function findArmChain(root: THREE.Object3D, side: ArmSide): ArmChain | null {
  const prefix = side === 'left' ? 'Left' : 'Right';
  const upper = findBone(root, `${prefix}Arm`);
  const mid = findBone(root, `${prefix}ForeArm`);
  const hand = findBone(root, `${prefix}Hand`);
  if (!upper || !mid || !hand) return null;
  return { upper, mid, hand };
}

function findBone(root: THREE.Object3D, shortName: string): THREE.Bone | null {
  let found: THREE.Bone | null = null;
  root.traverse((obj) => {
    if (found) return;
    const b = obj as THREE.Bone;
    if (!b.isBone) return;
    const n = b.name;
    if (
      n === shortName ||
      n === `mixamorig:${shortName}` ||
      n === `mixamorig_${shortName}` ||
      n.endsWith(`:${shortName}`) ||
      n.endsWith(`_${shortName}`)
    ) {
      found = b;
    }
  });
  return found;
}

/**
 * CCD two-bone IK: bend elbow then shoulder so the hand approaches `target`
 * (world space). `weight` blends how strongly we apply the correction (0–1).
 */
export function solveTwoBoneIK(
  chain: ArmChain,
  target: THREE.Vector3,
  weight: number,
  iterations = 5,
): void {
  if (weight <= 0.001) return;
  const w = Math.min(1, Math.max(0, weight));
  for (let i = 0; i < iterations; i++) {
    ccdRotate(chain.mid, chain.hand, target, w * 0.85);
    ccdRotate(chain.upper, chain.hand, target, w * 0.65);
  }
  chain.upper.updateMatrixWorld(true);
  chain.mid.updateMatrixWorld(true);
  chain.hand.updateMatrixWorld(true);
}

function ccdRotate(
  bone: THREE.Bone,
  effector: THREE.Bone,
  target: THREE.Vector3,
  weight: number,
): void {
  bone.getWorldPosition(_bonePos);
  effector.getWorldPosition(_effPos);
  _toEff.subVectors(_effPos, _bonePos);
  _toTgt.subVectors(target, _bonePos);
  if (_toEff.lengthSq() < 1e-8 || _toTgt.lengthSq() < 1e-8) return;
  _toEff.normalize();
  _toTgt.normalize();
  const dot = THREE.MathUtils.clamp(_toEff.dot(_toTgt), -1, 1);
  if (dot > 0.9995) return;
  _axis.crossVectors(_toEff, _toTgt);
  if (_axis.lengthSq() < 1e-10) return;
  _axis.normalize();
  const angle = Math.acos(dot) * weight;
  _q.setFromAxisAngle(_axis, angle);

  bone.getWorldQuaternion(_qWorld);
  _qWorld.premultiply(_q);
  if (bone.parent) {
    bone.parent.getWorldQuaternion(_qParent);
    _qLocal.copy(_qParent).invert().multiply(_qWorld);
  } else {
    _qLocal.copy(_qWorld);
  }
  bone.quaternion.slerp(_qLocal, weight);
  bone.updateMatrixWorld(true);
}

/** Soft ethereal tether between two world-space hand points. */
export class HandBond {
  readonly root = new THREE.Group();
  private readonly shaft: THREE.Mesh;
  private readonly glow: THREE.Mesh;
  private readonly mid = new THREE.Vector3();
  private readonly dir = new THREE.Vector3();
  private readonly quat = new THREE.Quaternion();
  private readonly yAxis = new THREE.Vector3(0, 1, 0);
  private visible = false;

  constructor() {
    const shaftMat = new THREE.MeshBasicMaterial({
      color: 0xffe2b8,
      transparent: true,
      opacity: 0.42,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 1, 8, 1, true), shaftMat);
    this.shaft.frustumCulled = false;

    const glowMat = new THREE.MeshBasicMaterial({
      color: 0xffd9a0,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.glow = new THREE.Mesh(new THREE.SphereGeometry(0.07, 12, 10), glowMat);
    this.glow.frustumCulled = false;

    this.root.add(this.shaft, this.glow);
    this.root.visible = false;
  }

  setActive(on: boolean): void {
    this.visible = on;
    this.root.visible = on;
  }

  update(a: THREE.Vector3, b: THREE.Vector3, pulse = 0): void {
    if (!this.visible) return;
    this.mid.lerpVectors(a, b, 0.5);
    this.dir.subVectors(b, a);
    const len = Math.max(0.05, this.dir.length());
    this.dir.normalize();
    this.quat.setFromUnitVectors(this.yAxis, this.dir);

    this.shaft.position.copy(this.mid);
    this.shaft.quaternion.copy(this.quat);
    this.shaft.scale.set(1, len, 1);

    this.glow.position.copy(this.mid);
    const s = 1 + Math.sin(pulse) * 0.12;
    this.glow.scale.setScalar(s);

    const shaftMat = this.shaft.material as THREE.MeshBasicMaterial;
    const glowMat = this.glow.material as THREE.MeshBasicMaterial;
    shaftMat.opacity = 0.28 + Math.sin(pulse * 1.3) * 0.08;
    glowMat.opacity = 0.45 + Math.sin(pulse * 1.7) * 0.12;
  }
}
